<template>
  <teleport to="body">
    <loading
      :active="isPaying"
      :is-full-page="fullPage"
      :z-index="1100"
    ></loading>
    <div ref="drawer" :class="['cart-drawer', { open: isOpen }]">
      <div class="cart-drawer-header">
        <h5>Il tuo carrello</h5>
        <button @click="close">✕</button>
      </div>

      <div class="cart-drawer-body">
        <div class="cart-drawer-scroll">
          <div v-if="isLoading" class="cart-loader">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Caricamento…</span>
            </div>
            <span class="mt-3 text-muted">Caricamento movimenti…</span>
          </div>

          <div v-else-if="!cartItems.length" class="empty">
            Il carrello è vuoto.
          </div>

          <div v-else>
            <CartAvvisi :avvisi="avvisoItems" />

            <div v-if="sospesoItems.length" class="mb-3">
              <button
                type="button"
                class="sospeso-toggle w-100 d-flex justify-content-between align-items-center"
                @click="showSospesi = !showSospesi"
              >
                <span>
                  <i
                    class="bi bi-exclamation-triangle-fill me-2 text-warning"
                  ></i>
                  Movimenti in sospeso ({{ sospesoItems.length }})
                </span>
                <i
                  :class="[
                    'bi',
                    showSospesi ? 'bi-chevron-up' : 'bi-chevron-down',
                  ]"
                ></i>
              </button>
            </div>

            <template v-for="(item, index) in displayedItems" :key="item.id">
              <hr
                v-if="
                  index > 0 &&
                  displayedItems[index - 1].sospeso &&
                  !item.sospeso
                "
              />
              <label
                v-if="
                  hasSelectable &&
                  !item.sospeso &&
                  (index === 0 || displayedItems[index - 1].sospeso)
                "
                class="form-check d-flex align-items-center mb-4 ps-2"
                style="cursor: pointer"
              >
                <input
                  type="checkbox"
                  class="form-check text-primary me-3"
                  :checked="allSelected"
                  @change="$store.dispatch('Cart/toggleAll')"
                />
                <span>
                  {{ allSelected ? "Deseleziona tutti" : "Seleziona tutti" }}
                </span>
              </label>
              <div
                :class="[
                  'border rounded-3 p-4 mb-3',
                  item.sospeso || !item.pagabile
                    ? 'bg-light-warning sospeso-card'
                    : item.required
                    ? 'bg-light-danger'
                    : item.pagabileSingolarmente
                    ? 'bg-light-success'
                    : 'bg-light',
                ]"
              >
                <label
                  :for="`chk-${item.id}`"
                  class="form-check d-flex align-items-start w-100 m-0"
                  :style="{
                    cursor:
                      item.sospeso ||
                      !item.pagabile ||
                      (item.required && requiredLocked)
                        ? 'default'
                        : 'pointer',
                  }"
                >
                  <input
                    v-if="item.pagabile"
                    type="checkbox"
                    class="form-check-input me-3 mt-1"
                    :class="
                      item.sospeso
                        ? 'text-warning'
                        : item.required
                        ? 'text-danger'
                        : 'text-primary'
                    "
                    :id="`chk-${item.id}`"
                    :checked="item.checked"
                    :disabled="
                      item.sospeso || (item.required && requiredLocked)
                    "
                    @change="$store.dispatch('Cart/toggleItem', item.id)"
                  />

                  <div class="w-100 d-flex justify-content-between">
                    <div class="pe-3">
                      {{ item.name }}
                      <br />
                      <small class="text-muted">
                        Data inserimento: {{ item.insertedAt || "—" }}
                      </small>
                    </div>

                    <div
                      class="text-end d-flex flex-column justify-content-between"
                    >
                      <span
                        v-if="item.pagabile"
                        class="fs-6 fw-bold text-nowrap"
                        :class="
                          item.sospeso
                            ? 'text-dark'
                            : item.required
                            ? 'text-danger'
                            : 'text-primary'
                        "
                      >
                        {{ item.price.toFixed(2) }} €
                      </span>
                      <small class="text-muted text-nowrap">
                        {{ item.type }} - {{ item.causale }}</small
                      >
                    </div>
                  </div>
                </label>
              </div>
            </template>

            <hr v-if="!hasPayable && showSospesi && sospesoItems.length" />

            <div v-if="!hasPayable" class="empty text-center py-4">
              Nessun movimento da pagare.
            </div>
          </div>
        </div>
        <div v-if="!isLoading && hasPayable" class="footer">
          <div class="d-flex justify-content-between fs-7 mb-1">
            <span>Totale movimenti selezionati:</span>
            <span>€ {{ format(subtotal) }}</span>
          </div>
          <div class="d-flex justify-content-between fs-7 mb-1">
            <span>{{ contoSportpay ? "Credito disponibile:" : "Saldo:" }}</span>
            <span>
              €
              {{
                format(
                  contoSportpay
                    ? Math.max(creditoDisponibile, 0)
                    : creditoDisponibile
                )
              }}
            </span>
          </div>
          <div
            class="d-flex justify-content-between total fs-5 fw-bold pt-2 border-top"
          >
            <span>Totale da pagare:</span>
            <span>€ {{ format(totaleDaPagare) }}</span>
          </div>

          <div class="d-flex">
            <button
              class="primary-trans w-50 me-2"
              @click="goToCart"
              :disabled="totalItems <= 0"
            >
              Espandi
            </button>
            <button
              class="primary w-50 ms-2 fw-bold"
              @click="payNow"
              :disabled="totalItems <= 0"
            >
              Paga ora
            </button>
          </div>
        </div>

        <div v-if="!isLoading && !hasPayable" class="footer">
          <button class="primary w-100 fw-bold" @click="close">Chiudi</button>
        </div>
      </div>
    </div>

    <AddPagamentoMovimenti
      ref="modalPagamento"
      :societa="idSocietaProp"
      :pagaSelezionati="pagaSelezionatiData"
      @refreshList="refreshCart"
      @setSpinnerView="onSpinnerView"
    />
  </teleport>
</template>

<script>
import Loading from "vue3-loading-overlay";
import "vue3-loading-overlay/dist/vue3-loading-overlay.css";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { Modal } from "bootstrap";
import AddPagamentoMovimenti from "@/components/components-fit/società/economato/AddPagamentoMovimenti.vue";
import CartAvvisi from "@/components/components-fit/cart/CartAvvisi.vue";
import { approvaMovimentiMultipla } from "@/requests/economato";

export default {
  components: { Loading, AddPagamentoMovimenti, CartAvvisi },
  data() {
    return { fullPage: true, showSospesi: false, pagaSelezionatiData: [] };
  },
  props: {
    contoSportpay: Boolean,
  },
  computed: {
    isOpen() {
      return this.$store.getters["Cart/drawerOpen"];
    },
    isPaying() {
      return this.$store.getters["Cart/isPaying"];
    },
    isLoading() {
      return this.$store.getters["Cart/isLoading"];
    },
    cartItems() {
      return this.$store.state.Cart.items || [];
    },
    subtotal() {
      return this.$store.getters["Cart/subtotal"];
    },
    creditoDisponibile() {
      return this.$store.getters["Cart/creditoDisponibile"];
    },
    totaleDaPagare() {
      return this.$store.getters["Cart/totaleDaPagare"];
    },

    totalItems() {
      return this.$store.getters["Cart/totalItems"];
    },

    allSelected() {
      return this.$store.getters["Cart/allSelected"];
    },

    hasSelectable() {
      return this.cartItems.some(
        (i) => !i.required && !i.sospeso && i.pagabile && !i.isAvviso
      );
    },

    requiredLocked() {
      return this.$store.getters["Cart/requiredLocked"];
    },

    avvisoItems() {
      return this.cartItems.filter((i) => i.isAvviso);
    },

    sospesoItems() {
      return this.cartItems.filter((i) => i.sospeso && !i.isAvviso);
    },

    payableItems() {
      return this.cartItems.filter((i) => !i.sospeso && !i.isAvviso);
    },

    hasPayable() {
      return this.payableItems.some((i) => i.pagabile);
    },

    displayedItems() {
      return this.showSospesi
        ? [...this.sospesoItems, ...this.payableItems]
        : this.payableItems;
    },

    checkedIds() {
      return this.cartItems
        .filter((i) => i.checked && i.pagabile)
        .map((i) => i.id);
    },

    saldoCopre() {
      return this.creditoDisponibile >= this.subtotal;
    },

    needsAddPagamentoModal() {
      return !this.contoSportpay && !this.saldoCopre;
    },

    idSocietaProp() {
      return { id: this.$store.getters.id_societa };
    },

    stagione() {
      return this.$store.getters.stagioneSelected;
    },
  },

  methods: {
    format(n) {
      return (Number(n) || 0).toFixed(2);
    },
    close() {
      this.$store.dispatch("Cart/closeDrawer");
    },

    goToCart() {
      this.close();
      this.$router.push({ name: "carrello" });
    },

    payNow() {
      if (!this.checkedIds.length) return;

      if (this.contoSportpay && this.totaleDaPagare > 0) {
        const importo = this.format(this.totaleDaPagare);
        Swal.fire({
          title: "Sei sicuro?",
          text: `Verranno addebitati, sul Borsellino FITP sportpay, € ${importo}`,
          icon: "warning",
          buttonsStyling: false,
          showCancelButton: true,
          cancelButtonText: "Annulla",
          confirmButtonText: "Procedi al pagamento",
          allowOutsideClick: false,
          customClass: {
            confirmButton: "btn fw-bold btn-light-primary",
            cancelButton: "btn fw-bold btn-light-danger",
          },
        }).then((e) => {
          if (e.isDismissed) return;
          this.$store.dispatch("Cart/checkout");
        });
        return;
      }

      if (this.saldoCopre) {
        this.pagaConSaldo();
      } else {
        this.pagaSelezionatiData = [...this.checkedIds];
        this.$nextTick(() => {
          const el = this.$refs.modalPagamento?.$el;
          if (el) Modal.getOrCreateInstance(el).show();
        });
      }
    },

    pagaConSaldo() {
      const importoSelezionati = this.subtotal;
      const saldo = this.creditoDisponibile;
      Swal.fire({
        title: "Introduzione pagamenti",
        html: `Il credito disponibile di ${saldo.toFixed(
          2
        )} € copre totalmente l'importo da pagare<br /><br /><b>Importo:</b> ${importoSelezionati.toFixed(
          2
        )} €`,
        showCancelButton: true,
        confirmButtonText: "Registra pagamento",
        cancelButtonText: "Annulla",
        customClass: {
          confirmButton: "btn fw-bold btn-light-primary",
          cancelButton: "btn fw-bold btn-light-danger",
        },
      }).then((result) => {
        if (!result.isConfirmed) return;
        const values = {
          arr_Movimenti: this.checkedIds.join(","),
          id_Societa: this.$store.getters.id_societa,
          importo: importoSelezionati,
          anno: this.stagione,
        };
        this.$store.commit("Cart/SET_PAYING", true);
        approvaMovimentiMultipla(values)
          .then((value) => {
            if (value?.data?.statusCode === 200) {
              Swal.fire({
                html: "Operazione completata",
                icon: "success",
                buttonsStyling: false,
                confirmButtonText: "OK",
                customClass: {
                  confirmButton: "btn fw-bold btn-light-danger",
                },
              });
              this.refreshCart();
            } else {
              Swal.fire({
                html:
                  value?.data?.message ||
                  "Attenzione! Si è verificato un errore. Riprovare più tardi",
                icon: "error",
                buttonsStyling: false,
                confirmButtonText: "OK",
                customClass: {
                  confirmButton: "btn fw-bold btn-light-danger",
                },
              });
            }
          })
          .finally(() => {
            this.$store.commit("Cart/SET_PAYING", false);
          });
      });
    },

    refreshCart() {
      this.$store.commit("Cart/BUMP_PAYMENT_TICK");
      this.$store.dispatch("Cart/loadCartData");
    },

    onSpinnerView(value) {
      this.$store.commit("Cart/SET_PAYING", !!value);
      if (!value) {
        const el = this.$refs.modalPagamento?.$el;
        if (el) Modal.getOrCreateInstance(el).hide();
      }
    },
  },

  mounted() {
    this._outside = (e) => {
      if (!this.isOpen) return;
      if (this.$refs.drawer.contains(e.target)) return;
      if (e.target.closest(".swal2-container")) return;
      if (e.target.closest(".modal")) return;
      if (e.target.closest(".modal-backdrop")) return;
      if (e.target.closest(".dp__outer_menu_wrap")) return;
      this.close();
    };

    document.addEventListener("mousedown", this._outside);
  },

  beforeUnmount() {
    document.removeEventListener("mousedown", this._outside);
  },
};
</script>

<style scoped>
.bg-light-danger {
  background-color: #fcd9e3 !important;
}

.bg-light-warning {
  background-color: #fff8dd !important;
}

.bg-light-success {
  background-color: #e8fff3 !important;
}

.sospeso-card {
  opacity: 0.8;
}

.sospeso-toggle {
  background: #fff8dd;
  color: #b78103;
  border: 1px solid #f6c000;
  border-radius: 8px;
  padding: 10px 14px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background 0.15s ease;
}

.sospeso-toggle:hover {
  background: #fdefa8;
}

.form-check-input.text-danger:checked {
  background-color: var(--bs-danger);
  border-color: var(--bs-danger);
}

.form-check-input.text-primary:checked {
  background-color: var(--bs-primary);
  border-color: var(--bs-primary);
}

.form-check-input.text-danger:disabled {
  opacity: 1;
}

.form-check-input[type="checkbox"] {
  border-radius: 50%;
}
</style>
<style>
.cart-drawer {
  display: flex;
  position: fixed;
  flex-direction: column;
  top: 0;
  right: 0;
  width: 420px;
  max-width: 95vw;
  height: 100vh;
  background: #fff;
  z-index: 9999;

  opacity: 0;
  pointer-events: none;
}

.cart-drawer.open {
  opacity: 1;
  box-shadow: -30px 0 60px rgba(0, 0, 0, 0.35);
  pointer-events: auto;
}
/* Header */
.cart-drawer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px;
  border-bottom: 1px solid #eee;
  font-weight: 600;
}

.cart-drawer-body {
  padding: 18px;
  display: flex;
  flex-direction: column;
  height: calc(100% - 70px);
}

.cart-drawer-scroll {
  flex: 1;
  overflow-y: auto;
  padding-right: 4px;
}

.footer {
  padding-top: 16px;
  border-top: 1px solid #eee;
  background: #fff;
}

.empty {
  color: #777;
}

.cart-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
}

/* Rows */
.cart-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.name {
  font-weight: 500;
}

.price {
  font-size: 0.85rem;
  color: #666;
}

.controls button {
  margin: 0 4px;
  cursor: pointer;
}

/* Footer */
.footer {
  margin-top: auto;
  padding-top: 16px;
  border-top: 1px solid #eee;
}

.total {
  margin-bottom: 12px;
  font-weight: 600;
}

.primary,
.primary:active,
.primary:focus {
  width: 100%;
  padding: 10px;
  background: #c41e3a;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.primary:hover {
  opacity: 0.9;
}

.primary-trans {
  width: 100%;
  padding: 10px;
  background: white;
  color: #c41e3a;
  border: #c41e3a 1px solid;
  border-radius: 4px;
  cursor: pointer;
}

.primary-trans:hover {
  opacity: 0.9;
}
</style>
